'use client'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { memo, ReactElement, ReactNode, useEffect, useRef } from 'react'
import { tss } from 'tss-react/mui'
import { WatchLaterIcon } from '../Icon/catalog'
import Bubble from './Bubble'

const DEFAULT_HOST_NAME = 'www.internations.org'

export type ChatBubbleProps = {
    subject?: string | ReactNode
    text?: string | ReactNode
    name?: string
    avatar?: ReactNode
    time: string
    inverted?: boolean
    showArrow?: boolean
    gutterBottom?: boolean
    action?: ({ inverted }: { inverted: boolean }) => ReactNode
    customContent?: ReactNode
    pending?: boolean
    className?: string
}

const useStyles = tss
    .withName('ChatBubble')
    .withNestedSelectors<'action' | 'gutterBottom' | 'inverted'>()
    .create(({ theme, classes }) => ({
        root: {
            display: 'flex',
            marginBottom: 0,

            [`&.${classes.gutterBottom}`]: {
                marginBottom: theme.spacing(3),
            },

            [`&.${classes.inverted}`]: {
                justifyContent: 'flex-end',
            },
        },
        textWrapper: {
            color: 'inherit',

            '& > p': {
                marginTop: 0,
                marginBottom: theme.spacing(1),
                lineHeight: '20px',

                '&:last-of-type': {
                    marginBottom: 0,
                },
            },
        },
        watchLaterIcon: {
            float: 'left',
            width: '13px',
            height: '13px',
            marginTop: theme.spacing(0.1),
            marginRight: theme.spacing(0.4),
        },
        subtitleTwo: {
            paddingBottom: theme.spacing(0.5),
            lineHeight: '18px',
        },
        actionWrappr: {
            position: 'absolute',
            top: 8,
            right: 0,
        },
        nameWrapper: {
            paddingRight: theme.spacing(4),

            [`&.${classes.action}`]: {
                // min-height prevents the button from running onto the time stamp
                minHeight: theme.spacing(3.5),
            },
        },
        timeWrapper: {
            display: 'flex',
            marginRight: theme.spacing(0.25),
            marginTop: theme.spacing(0.5),
        },
        time: {
            color: theme.palette.text.secondary,

            [`&.${classes.inverted}`]: {
                color: '#eeeeee',
            },
        },
        timePending: {
            textAlign: 'left',
            paddingRight: theme.spacing(2),
        },
        timeNotPending: {
            textAlign: 'right',
            flexGrow: 1,
        },
        avatarWrapper: {
            marginRight: theme.spacing(1),

            [`&.${classes.inverted}`]: {
                marginRight: 0,
                marginLeft: theme.spacing(1),
            },
        },
        action: {},
        gutterBottom: {},
        inverted: {},
    }))

function UnmemoizedChatBubble({
    subject,
    text,
    name,
    avatar,
    time,
    inverted = false,
    showArrow = true,
    gutterBottom = false,
    action,
    customContent,
    pending,
    className,
}: ChatBubbleProps): ReactElement {
    const { classes, cx } = useStyles()
    const textEl = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!textEl.current) {
            return
        }

        Array.from(textEl.current.getElementsByTagName('a')).forEach(function (a: HTMLAnchorElement) {
            if (a.hostname === DEFAULT_HOST_NAME || `www.${a.hostname}` === DEFAULT_HOST_NAME) {
                a.removeAttribute('target')

                return
            }

            a.setAttribute('target', '_blank')
            a.setAttribute('rel', 'noopener noreferrer')
        })
    }, [textEl])

    const hasTextContent = Boolean(text)
    const TextContent = () => {
        if (!hasTextContent) {
            return
        }

        if (typeof text === 'string') {
            const textWrapperInnerHTML = hasTextContent
                ? ({
                      __html: String(text),
                  } as const)
                : undefined

            return (
                <Typography
                    component="div" // Cannot have the default <p> component, since text might contain paragraphs
                    variant="body2"
                    className={cx(classes.textWrapper, 'word-break')}
                    dangerouslySetInnerHTML={textWrapperInnerHTML}
                    ref={textEl}
                />
            )
        }

        return <Box className="word-break">{text}</Box>
    }
    const content = (
        <>
            {action ? <div className={classes.actionWrappr}>{action({ inverted })}</div> : null}
            {name || hasTextContent ? (
                <div className={cx(classes.nameWrapper, action && classes.action)}>
                    {name ? (
                        <Typography variant="h4" gutterBottom className="line-clamp-1">
                            {name}
                        </Typography>
                    ) : null}
                    {subject ? (
                        <Typography variant="subtitle2" className={classes.subtitleTwo}>
                            {subject}
                        </Typography>
                    ) : null}
                    <TextContent />
                </div>
            ) : null}
            {customContent}
            <div className={classes.timeWrapper}>
                {pending ? (
                    <Typography
                        variant="caption"
                        className={cx(classes.time, classes.timePending, inverted && classes.inverted)}
                    >
                        <WatchLaterIcon className={classes.watchLaterIcon} />
                        <span>Pending...</span>
                    </Typography>
                ) : null}
                <Typography
                    variant="caption"
                    className={cx(classes.time, classes.timeNotPending, inverted && classes.inverted)}
                >
                    {time}
                </Typography>
            </div>
        </>
    )
    const avatarContents = avatar ? (
        <Box className={cx(classes.avatarWrapper, inverted && classes.inverted)}>{avatar}</Box>
    ) : null

    if (inverted) {
        return (
            <div className={cx(classes.root, classes.inverted, gutterBottom && classes.gutterBottom, className)}>
                <Bubble inverted showArrow={showArrow} pending={pending}>
                    {content}
                </Bubble>
                {avatarContents}
            </div>
        )
    }

    return (
        <div className={cx(classes.root, gutterBottom && classes.gutterBottom, className)}>
            {avatarContents}
            <Bubble showArrow={showArrow}>{content}</Bubble>
        </div>
    )
}

function areChatBubblesEqual(prev: ChatBubbleProps, next: ChatBubbleProps): boolean {
    return prev.time === next.time && prev.pending === next.pending && prev.action === next.action
}

const ChatBubble = memo(UnmemoizedChatBubble, areChatBubblesEqual)

export default ChatBubble
